import { css } from '@emotion/react';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback,
  useMemo,
} from 'react';
import {
  DropDownItemSize, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { InputSize } from '~/_shared/baseComponents/inputs';
import {
  TableDataCellComponent, TableRowComponent,
} from '~/_shared/baseComponents/table';
import type { EntityAccessInvitation } from '~/_shared/entityAccess/entityAccess.invitations.repository';
import {
  type EntityAccess, EntityAccessLevel,
} from '~/_shared/entityAccess/entityAccess.repository';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { MemberIndexCircleComponent } from '~/clientTeamManagement/memberIndexCircle.component';
import { getTeamShareMapPermissionOptions } from '../teamShareMap.helpers';
import { TeamShareMapPermission } from '../teamShareMap.types';
import { type MapAccessData } from '../teamShareMapModal.container';

const rowStyle = (theme: Theme) => css({
  background: theme.modalColors.contentBackground,
  borderBottom: `1px solid ${theme.borderColors.primary}`,
});

const memberNameStyle = (theme: Theme) => css({
  color: theme.textColors.primary,
});

const memberEmailStyle = (theme: Theme) => css({
  color: theme.textColors.primary,
});

const iconStyle = (theme: Theme) => css({
  fontSize: '20px',
  color: theme.iconColors.danger,
  cursor: 'pointer',
});

const pendingStyle = (theme: Theme) => css({
  fontSize: 16,
  color: theme.textColors.warning,
  paddingLeft: 7,
});

const getTeamSharePermissionFromMapAccess = (item: MapAccessData): TeamShareMapPermission => {
  const ownerId = item.entityAccess?.ownerId;
  const sharedEntityId = item.entityAccess?.sharedEntityId ?? item.invitation?.sharedEntityId;
  const accessLevel = item.entityAccess?.accessLevel ?? item.invitation?.accessLevel;
  if (sharedEntityId && accessLevel) {
    if (ownerId === sharedEntityId) {
      return TeamShareMapPermission.Owner;
    }
    if (accessLevel === EntityAccessLevel.WRITE) {
      return TeamShareMapPermission.Contributor;
    }
  }
  return TeamShareMapPermission.ViewOnly;
};

type MembersTableItemProps = {
  currentUserId: number;
  index: number;
  item: MapAccessData;
  onUpdateAccess: (item: EntityAccess, newPermission: TeamShareMapPermission) => void;
  onDeleteAccess: (item: EntityAccess) => void;
  onDeleteInvitation: (item: EntityAccessInvitation) => void;
};

export const MembersTableItem: FC<MembersTableItemProps> = (props) => {
  const { currentUserId, index, item, onUpdateAccess, onDeleteAccess, onDeleteInvitation } = props;
  const [t] = useTranslation();

  const handlePermissionChange = useCallback((newPermission: TeamShareMapPermission) => {
    if (item.entityAccess) {
      onUpdateAccess(item.entityAccess, newPermission);
    }
  }, [onUpdateAccess, item]);

  const permissionOptions = useMemo(() => getTeamShareMapPermissionOptions(t, true), [t]);

  return (
    <TableRowComponent css={rowStyle}>
      <TableDataCellComponent css={{ textAlign: 'left' }}>
        <MemberIndexCircleComponent number={index} />
      </TableDataCellComponent>
      <TableDataCellComponent css={{ textAlign: 'left' }}>
        <span css={memberNameStyle}>{item.name}</span>
      </TableDataCellComponent>
      <TableDataCellComponent>
        <span css={memberEmailStyle}>{item.email}</span>
        {!!item.invitation && (
          <span css={pendingStyle}>({t('Pending')})</span>
        )}
      </TableDataCellComponent>
      <TableDataCellComponent css={{ textAlign: 'right' }}>
        <RegularDropdownComponent
          value={getTeamSharePermissionFromMapAccess(item)}
          onChange={handlePermissionChange}
          options={permissionOptions}
          inputSize={InputSize.Small}
          itemSize={DropDownItemSize.Large}
          isDisabled={!item.entityAccess || item.entityAccess.sharedEntityId === currentUserId}
        />
      </TableDataCellComponent>
      <TableDataCellComponent css={{ textAlign: 'left', padding: 0 }}>
        {item.entityAccess && item.entityAccess.sharedEntityId !== currentUserId && (
          <div
            onClick={() => {
              if (item.entityAccess) {
                onDeleteAccess(item.entityAccess);
              }
            }}
          >
            <FontAwesomeIcon
              icon={faTrash}
              css={iconStyle}
            />
          </div>
        )}
        {!!item.invitation && (
          <div
            onClick={() => {
              if (item.invitation) {
                onDeleteInvitation(item.invitation);
              }
            }}
          >
            <FontAwesomeIcon
              icon={faTrash}
              css={iconStyle}
            />
          </div>
        )}
      </TableDataCellComponent>
    </TableRowComponent>
  );
};
